import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useEffect } from 'react';

const PoliticaPrivacidadePage = () => {
  useEffect(() => {
    const jsonLdData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Política de Privacidade - Target Software",
      "description": "Política de privacidade da Target Software explicando como tratamos suas informações pessoais e protegemos sua privacidade.",
      "url": "https://targetsoftware.com.br/politica-privacidade",
      "mainEntity": {
        "@type": "PrivacyPolicy",
        "name": "Política de Privacidade",
        "text": "A Target Software respeita sua privacidade e está comprometida em proteger suas informações pessoais. Durante a navegação em nosso site, nenhum dado pessoal do usuário é armazenado em nossos servidores.",
        "datePublished": "2025-11-13",
        "publisher": {
          "@type": "Organization",
          "name": "Target Software",
          "url": "https://targetsoftware.com.br"
        }
      },
      "publisher": {
        "@type": "Organization",
        "name": "Target Software",
        "description": "Empresa especializada em soluções SaaS para incentivos comerciais, gestão de parceiros e business intelligence.",
        "url": "https://targetsoftware.com.br",
        "logo": "https://targetsoftware.com.br/images/logos/logo-target.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "comercial@targetsoftware.com.br",
          "contactType": "customer service",
          "availableLanguage": "Portuguese"
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header startInDarkMode={false} />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#F6F6F6] to-[#E3E3E3] overflow-hidden">
        <div className="absolute inset-0">
          {/* Enhanced Floating Elements */}
          <div className="absolute top-20 left-10 w-2 h-2 bg-[#7d2128] rounded-full animate-pulse opacity-60"></div>
          <div className="absolute top-40 right-20 w-1 h-1 bg-gray-400 rounded-full animate-ping opacity-40"></div>

          {/* New Horizontal Moving Elements */}
          <div className="absolute top-16 left-0 w-4 h-4 bg-[#7d2128]/20 animate-move-right opacity-40"></div>
          <div className="absolute top-32 left-0 w-2 h-2 bg-gray-500/30 animate-move-right-slow opacity-50" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-48 left-0 w-3 h-3 bg-[#7d2128]/30 animate-move-right-fast opacity-60" style={{ animationDelay: '2s' }}></div>

          {/* New Elements Moving from Right */}
          <div className="absolute top-24 right-0 w-4 h-4 bg-gray-600/25 animate-move-left opacity-45"></div>
          <div className="absolute top-40 right-0 w-2 h-2 bg-[#7d2128]/35 animate-move-left-fast opacity-55" style={{ animationDelay: '0.8s' }}></div>
          <div className="absolute bottom-28 right-0 w-3 h-3 bg-gray-500/30 animate-move-left-slow opacity-40" style={{ animationDelay: '1.2s' }}></div>

          {/* Enhanced Vertical Moving Elements */}
          <div className="absolute top-0 left-16 w-6 h-6 bg-[#7d2128]/15 rounded-full animate-move-down opacity-40"></div>
          <div className="absolute top-0 left-32 w-4 h-4 bg-gray-500/20 rounded-full animate-move-down-slow opacity-50" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-0 left-48 w-3 h-3 bg-[#7d2128]/25 rounded-full animate-move-down-fast opacity-60" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-0 right-16 w-5 h-5 bg-gray-400/18 rounded-full animate-move-down opacity-35" style={{ animationDelay: '1.5s' }}></div>

          {/* New Elements Moving Up */}
          <div className="absolute bottom-0 left-24 w-5 h-5 bg-gray-500/20 rounded-full animate-move-up opacity-40"></div>
          <div className="absolute bottom-0 left-40 w-3 h-3 bg-[#7d2128]/28 rounded-full animate-move-up-slow opacity-50" style={{ animationDelay: '0.7s' }}></div>
          <div className="absolute bottom-0 right-24 w-4 h-4 bg-gray-400/25 rounded-full animate-move-up-fast opacity-45" style={{ animationDelay: '1.3s' }}></div>

          {/* Rotating Elements */}
          <div className="absolute top-1/4 left-12 w-8 h-8 bg-[#7d2128]/10 animate-rotate-slow opacity-30"></div>
          <div className="absolute top-3/4 right-12 w-6 h-6 bg-gray-500/15 animate-rotate-reverse opacity-40" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-20 w-4 h-4 bg-[#7d2128]/20 animate-rotate-fast opacity-50" style={{ animationDelay: '0.5s' }}></div>

          {/* Floating Geometric Shapes */}
          <div className="absolute top-32 left-1/4 w-3 h-3 bg-[#7d2128]/25 rotate-45 animate-float opacity-45"></div>
          <div className="absolute bottom-32 right-1/4 w-4 h-4 bg-gray-500/20 rotate-45 animate-float-slow opacity-40" style={{ animationDelay: '0.8s' }}></div>

          {/* Additional Circuit-like Elements */}
          <div className="absolute top-20 right-1/3 w-6 h-1 bg-[#7d2128]/30 animate-pulse opacity-40" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute bottom-20 left-1/3 w-8 h-1 bg-gray-500/25 animate-pulse opacity-35" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-8 w-4 h-1 bg-[#7d2128]/40 animate-pulse opacity-50" style={{ animationDelay: '1.5s' }}></div>

          {/* Corner Elements */}
          <div className="absolute top-4 left-4 w-2 h-2 bg-[#7d2128]/20 animate-ping opacity-60" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-4 right-4 w-3 h-3 bg-gray-500/15 animate-bounce opacity-40" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 mt-9">
              Política de <span className="text-[#7d2128]">Privacidade</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Sua privacidade é importante para nós. <br/>Saiba como tratamos suas informações pessoais.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">

            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Coleta de Dados</h2>
            <p className="text-gray-700 mb-6">
              A Target Software respeita sua privacidade e está comprometida em proteger suas informações pessoais.
              Durante a navegação em nosso site, <strong>nenhum dado pessoal do usuário é armazenado</strong> em nossos servidores.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Uso de Cookies</h2>
            <p className="text-gray-700 mb-6">
              Utilizamos cookies apenas para melhorar sua experiência de navegação e analisar o tráfego do site.
              Estes cookies não coletam informações pessoais identificáveis e são utilizados exclusivamente para
              fins analíticos e de melhoria do serviço.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Formulário de Contato</h2>
            <p className="text-gray-700 mb-6">
              Quando você preenche o formulário de contato em nosso site, entende-se que você <strong>autoriza expressamente
              o contato</strong> pela Target Software. As informações fornecidas serão utilizadas exclusivamente para:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6">
              <li>Responder às suas dúvidas e solicitações</li>
              <li>Fornecer informações sobre nossos produtos e serviços</li>
              <li>Agendar demonstrações ou reuniões quando solicitado</li>
              <li>Enviar comunicações relevantes sobre nossas soluções</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Armazenamento de Dados</h2>
            <p className="text-gray-700 mb-6">
              As informações fornecidas através do formulário de contato são armazenadas de forma segura em nossos
              sistemas apenas pelo tempo necessário para atender sua solicitação. Não compartilhamos suas informações
              com terceiros sem sua autorização prévia.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Segurança</h2>
            <p className="text-gray-700 mb-6">
              Implementamos medidas de segurança técnicas e administrativas apropriadas para proteger suas informações
              pessoais contra acesso não autorizado, alteração, divulgação ou destruição.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Seus Direitos</h2>
            <p className="text-gray-700 mb-6">
              Você tem o direito de solicitar a exclusão de seus dados pessoais de nossos sistemas a qualquer momento.
              Para exercer este direito, entre em contato conosco através do formulário disponível em nosso site.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Alterações nesta Política</h2>
            <p className="text-gray-700 mb-6">
              Podemos atualizar esta Política de Privacidade periodicamente. Recomendamos que você revise esta página
              regularmente para se manter informado sobre como protegemos suas informações.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contato</h2>
            <p className="text-gray-700 mb-6">
              Se você tiver dúvidas sobre esta Política de Privacidade ou sobre como tratamos suas informações,
              entre em contato conosco através do formulário disponível em nosso site.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mt-8">
              <p className="text-sm text-gray-600">
                <strong>Última atualização:</strong> 13 de novembro de 2025
              </p>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PoliticaPrivacidadePage;